import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Video, Plus, Filter } from "lucide-react";
import { toast } from "sonner";
import { TrainingVideoCard } from "./TrainingVideoCard";
import { TrainingVideoPlayer } from "./TrainingVideoPlayer";
import { TrainingVideoUploadDialog } from "./TrainingVideoUploadDialog";
import { TrainingVideoEditDialog } from "./TrainingVideoEditDialog";
import { TrainingVideoDeleteDialog } from "./TrainingVideoDeleteDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface TrainingVideo {
  id: string;
  title: string;
  description: string | null;
  video_path: string;
  thumbnail_path: string | null;
  video_size: number;
  category: string | null;
  is_mandatory: boolean;
  mandatory_for_departments: string[];
  created_at: string;
  work_process_id: string | null;
}

interface TrainingVideoDocument {
  id: string;
  video_id: string;
  file_name: string;
  file_path: string;
  description: string | null;
}

interface TrainingVideoCompletion {
  video_id: string;
  user_id: string;
}

interface TrainingVideosSectionProps {
  workProcessId?: string;
  departmentId?: string;
}

export function TrainingVideosSection({ workProcessId, departmentId }: TrainingVideosSectionProps) {
  const { user, userRole, profile } = useAuth();
  const [videos, setVideos] = useState<TrainingVideo[]>([]);
  const [documents, setDocuments] = useState<TrainingVideoDocument[]>([]);
  const [completions, setCompletions] = useState<TrainingVideoCompletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState<TrainingVideo | null>(null);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [videoToEdit, setVideoToEdit] = useState<TrainingVideo | null>(null);
  const [videoToDelete, setVideoToDelete] = useState<TrainingVideo | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [userDepartmentId, setUserDepartmentId] = useState<string | null>(null);

  const isAdmin = userRole === 'admin' || userRole === 'hr_manager';

  useEffect(() => {
    loadData();
    loadUserDepartment();
  }, [workProcessId]);

  const loadUserDepartment = async () => {
    if (!profile?.employee_id) return;
    try {
      const { data } = await supabase
        .from('employees')
        .select('department')
        .eq('id', profile.employee_id)
        .single();
      
      if (data?.department) {
        const { data: deptData } = await supabase
          .from('departments')
          .select('id')
          .eq('name', data.department)
          .single();
        
        if (deptData) {
          setUserDepartmentId(deptData.id);
        }
      }
    } catch (error) {
      console.error('Error loading user department:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Load videos
      let query = supabase.from('training_videos').select('*').order('sort_order');
      
      if (workProcessId) {
        query = query.eq('work_process_id', workProcessId);
      }

      const { data: videosData, error: videosError } = await query;
      if (videosError) throw videosError;
      
      // Parse mandatory_for_departments array
      const parsedVideos = (videosData || []).map(v => ({
        ...v,
        mandatory_for_departments: v.mandatory_for_departments || []
      }));
      setVideos(parsedVideos);

      // Load documents
      if (parsedVideos.length > 0) {
        const videoIds = parsedVideos.map(v => v.id);
        const { data: docsData } = await supabase
          .from('training_video_documents')
          .select('*')
          .in('video_id', videoIds);
        setDocuments(docsData || []);
      }

      // Load user's completions
      if (user) {
        const { data: completionsData } = await supabase
          .from('training_video_completions')
          .select('video_id, user_id');
        setCompletions(completionsData || []);
      }
    } catch (error) {
      console.error('Error loading training videos:', error);
      toast.error("Fehler beim Laden der Lernvideos");
    } finally {
      setLoading(false);
    }
  };

  const handleWatch = (video: TrainingVideo) => {
    setSelectedVideo(video);
    setPlayerOpen(true);
  };

  const handleEdit = (video: TrainingVideo) => {
    setVideoToEdit(video);
    setEditOpen(true);
  };

  const handleDelete = (video: TrainingVideo) => {
    setVideoToDelete(video);
    setDeleteOpen(true);
  };

  const handleConfirmWatched = async () => {
    if (!selectedVideo || !user) return;

    try {
      const { error } = await supabase.from('training_video_completions').insert({
        video_id: selectedVideo.id,
        user_id: user.id,
        employee_id: profile?.employee_id || null,
      });

      if (error && !error.message.includes('duplicate')) throw error;

      // Update local state
      setCompletions(prev => [...prev, { video_id: selectedVideo.id, user_id: user.id }]);
    } catch (error) {
      console.error('Error confirming video:', error);
      throw error;
    }
  };

  const handleDownloadDocument = async (doc: TrainingVideoDocument) => {
    try {
      const { data, error } = await supabase.storage
        .from('training-videos')
        .download(doc.file_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error("Fehler beim Herunterladen");
    }
  };

  const isVideoCompleted = (videoId: string) => {
    return completions.some(c => c.video_id === videoId && c.user_id === user?.id);
  };

  const isVideoMandatoryForUser = (video: TrainingVideo) => {
    if (!video.is_mandatory) return false;
    if (video.mandatory_for_departments.length === 0) return true;
    return userDepartmentId ? video.mandatory_for_departments.includes(userDepartmentId) : false;
  };

  const getVideoDocuments = (videoId: string) => {
    return documents.filter(d => d.video_id === videoId);
  };

  const categories = [...new Set(videos.filter(v => v.category).map(v => v.category!))];

  const filteredVideos = categoryFilter === "all" 
    ? videos 
    : videos.filter(v => v.category === categoryFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">Lernvideos werden geladen...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Video className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Lernvideos</h3>
          <Badge variant="secondary">{videos.length}</Badge>
        </div>

        <div className="flex items-center gap-2">
          {categories.length > 0 && (
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Alle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kategorien</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {isAdmin && (
            <Button onClick={() => setUploadOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Video hinzufügen
            </Button>
          )}
        </div>
      </div>

      {/* Videos Grid */}
      {filteredVideos.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Video className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Keine Lernvideos</h3>
            <p className="text-muted-foreground text-center">
              {isAdmin
                ? "Laden Sie Lernvideos hoch, um Mitarbeiter zu schulen."
                : "Für diesen Bereich wurden noch keine Lernvideos bereitgestellt."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredVideos.map(video => (
              <TrainingVideoCard
              key={video.id}
              video={video}
              documents={getVideoDocuments(video.id)}
              isCompleted={isVideoCompleted(video.id)}
              isMandatoryForUser={isVideoMandatoryForUser(video)}
              onWatch={handleWatch}
              onDownloadDocument={handleDownloadDocument}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isAdmin={isAdmin}
              completionCount={completions.filter(c => c.video_id === video.id).length}
            />
          ))}
        </div>
      )}

      {/* Video Player */}
      <TrainingVideoPlayer
        open={playerOpen}
        onOpenChange={setPlayerOpen}
        video={selectedVideo}
        isCompleted={selectedVideo ? isVideoCompleted(selectedVideo.id) : false}
        onConfirmWatched={handleConfirmWatched}
      />

      {/* Upload Dialog */}
      {isAdmin && (
        <TrainingVideoUploadDialog
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          workProcessId={workProcessId}
          onSuccess={loadData}
        />
      )}

      {/* Edit Dialog */}
      {isAdmin && (
        <TrainingVideoEditDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          video={videoToEdit}
          onSuccess={loadData}
        />
      )}

      {/* Delete Dialog */}
      {isAdmin && (
        <TrainingVideoDeleteDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          video={videoToDelete}
          onSuccess={loadData}
        />
      )}
    </div>
  );
}
